import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import Model from "@tripian/model";
import { api } from "@tripian/core";

export interface TourApiCategoryCatalog {
  id: string;
  items: Model.TourSearchProduct[];
}

export interface UseTourApiCategoryCatalogsParams {
  /** İstek atılacak kategori id'leri (tour-api'nin `categories` parametresi) */
  categoryIds: string[];
  cityId?: string;
  providerId?: string;
  lat?: number;
  lng?: number;
  date?: string;
  to?: string;
  adults?: number;
  currency?: string;
  lang?: string;
  /** Kategori başına çekilecek ürün sayısı. instantAvailability açıkken API 10'a kırpar. */
  limit?: number;
}

/**
 * Kategori başına bir POST /tour-api/search isteği atar ve sonuçları kategori
 * gruplarına ayrılmış halde döndürür.
 *
 * Neden kategori başına ayrı istek: legacy LocalExperiences sayfası ürünleri sabit
 * kategori satırları halinde (yatay slider) gösteriyor, ama tour-api yanıtında
 * kategori alanı yok — yalnızca serbest metin `tags` var. Tek bir istekten gelen
 * ürünleri client-side kategorilere dağıtmak güvenilir olmadığı için gruplama
 * sunucuya bırakılıyor: her satır kendi `categories` filtresiyle çekiliyor.
 *
 * Sıralama da sunucu tarafında (fiyat artan) — legacy sayfa her kategoriyi fiyata
 * göre artan sıralıyordu, o davranış korunuyor.
 */
const useTourApiCategoryCatalogs = (
  params: UseTourApiCategoryCatalogsParams
) => {
  const {
    categoryIds,
    cityId,
    providerId,
    lat,
    lng,
    date,
    to,
    adults = 1,
    currency = "usd",
    lang = "en",
    limit = 10,
  } = params;

  const [catalogs, setCatalogs] = useState<TourApiCategoryCatalog[]>([]);

  // Yanıtlar geldiğinde arama parametreleri değişmişse eski sonuçları yazmamak için
  const generationRef = useRef<number>(0);

  const isActive =
    typeof window !== "undefined" &&
    !!window.tconfig?.SHOW_TOURS_AND_TICKETS &&
    providerId !== undefined &&
    !!cityId &&
    lat !== undefined &&
    lng !== undefined &&
    categoryIds.length > 0;

  // İlk render'da true: aksi halde effect çalışana kadar catalogs boş + loading false
  // olur ve sayfa bir an "tur bulunamadı" ekranını gösterirdi.
  const [loading, setLoading] = useState<boolean>(isActive);

  // Dizi/obje referansları her render'da yenilendiği için effect'i string anahtara bağlıyoruz
  const searchKey = useMemo(
    () =>
      JSON.stringify({
        categoryIds,
        cityId,
        providerId,
        lat,
        lng,
        date,
        to,
        adults,
        currency,
        lang,
        limit,
      }),
    [categoryIds, cityId, providerId, lat, lng, date, to, adults, currency, lang, limit]
  );

  const fetchCatalogs = useCallback(async () => {
    if (!isActive) {
      setCatalogs([]);
      setLoading(false);
      return;
    }

    const currentGeneration = generationRef.current + 1;
    generationRef.current = currentGeneration;

    setLoading(true);

    // Geçmiş bir tarihle arama yapılırsa API boş döner; diğer tur hook'ları gibi bugüne çekiyoruz
    const today = moment().startOf("day");
    const clampedDate =
      date && moment(date).isBefore(today)
        ? today.format("YYYY-MM-DD")
        : date || today.format("YYYY-MM-DD");

    const results = await Promise.all(
      categoryIds.map(categoryId => {
        const request: Partial<Model.TourSearchRequest> = {
          cityId,
          providerId,
          lat,
          lng,
          date: clampedDate,
          adults,
          currency,
          lang,
          categories: categoryId,
          sortingBy: "price",
          sortingType: "asc",
          offset: 0,
          limit,
          instantAvailability: window.tconfig?.TOUR_INSTANT_AVAILABILITY ? 1 : 0,
          ...(to ? { to } : {}),
        };

        return api
          .tourSearch(request as Model.TourSearchRequest)
          .then(result => ({
            id: categoryId,
            items: result.products || [],
          }))
          .catch((err: any) => {
            // Bir kategorinin hatası diğerlerini düşürmesin
            console.error(`Tour search failed for category ${categoryId}:`, err);
            return { id: categoryId, items: [] as Model.TourSearchProduct[] };
          });
      })
    );

    if (currentGeneration !== generationRef.current) return;

    setCatalogs(results);
    setLoading(false);
  }, [
    isActive,
    categoryIds,
    cityId,
    providerId,
    lat,
    lng,
    date,
    to,
    adults,
    currency,
    lang,
    limit,
  ]);

  useEffect(() => {
    fetchCatalogs();
    // fetchCatalogs kimliği yukarıdaki tüm parametrelere bağlı; searchKey onu tek
    // bir değere indirip aynı aramanın tekrar tetiklenmesini engelliyor.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchKey]);

  return { catalogs, loading };
};

export default useTourApiCategoryCatalogs;
